import { sym } from './support.symbol.js';
import { enumify } from '#library/enumerate.library.js';
import { proxify } from '#library/proxy.library.js';
import { allDescriptors } from '#library/reflection.library.js';
import { looseIndex } from '#library/object.library.js';
/** calendar seasons */
export const SEASON = enumify({
    Spring: 'spring',
    Summer: 'summer',
    Autumn: 'autumn',
    Winter: 'winter'
}, false);
/** cardinal directions */
export const COMPASS = looseIndex()(enumify({
    North: 'north',
    South: 'south',
    East: 'east',
    West: 'west'
}, false));
/**
 * Various enumerations used throughout Tempo library.
 * These are exported and added as static getters of the Tempo class.
 */
/** @internal LIVE state for all registries */
export const DEFAULTS = {
    NUMBER: {
        zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10
    },
    DURATION: {
        /** approx number of seconds in a year */ year: 31_536_000,
        /** approx number of seconds in a month */ month: 2_628_000,
        /** number of seconds in a week */ week: 604_800,
        /** number of seconds in a day */ day: 86_400,
        /** number of seconds in an hour */ hour: 3_600,
        /** number of seconds in a minute */ minute: 60,
        /** one second */ second: 1,
        /** number of seconds in a millisecond */ millisecond: .001,
        /** number of seconds in a microsecond */ microsecond: .000_001,
        /** number of seconds in a nanosecond */ nanosecond: .000_000_001,
    },
    TIMEZONE: {
        'utc': 'UTC',
        'gmt': 'UTC',
        'est': 'America/New_York',
        'edt': 'America/New_York',
        'cst': 'America/Chicago',
        'cdt': 'America/Chicago',
        'mst': 'America/Denver',
        'mdt': 'America/Denver',
        'pst': 'America/Los_Angeles',
        'pdt': 'America/Los_Angeles',
        'aest': 'Australia/Sydney',
        'aedt': 'Australia/Sydney',
        'acst': 'Australia/Adelaide',
        'acdt': 'Australia/Adelaide',
        'awst': 'Australia/Perth',
        'nzt': 'Pacific/Auckland',
        'nzst': 'Pacific/Auckland',
        'nzdt': 'Pacific/Auckland',
        'cet': 'Europe/Paris',
        'cest': 'Europe/Paris',
        'eet': 'Europe/Helsinki',
        'eest': 'Europe/Helsinki',
        'ist': 'Asia/Kolkata',
        'npt': 'Asia/Kathmandu',
        'jst': 'Asia/Tokyo',
    },
    DURATIONS: {
        /** approx number of milliseconds in a year */ years: 31_536_000_000,
        /** approx number of milliseconds in a month */ months: 2_628_000_000,
        /** number of milliseconds in a week */ weeks: 604_800_000,
        /** number of milliseconds in a day */ days: 86_400_000,
        /** number of milliseconds in an hour */ hours: 3_600_000,
        /** number of milliseconds in a minute */ minutes: 60_000,
        /** number of milliseconds in a second */ seconds: 1_000,
        /** one millisecond */ milliseconds: 1,
        /** number of milliseconds in a microsecond */ microseconds: .001,
        /** number of milliseconds in a nanosecond */ nanoseconds: .000_001,
    },
    FORMAT: {
        /** useful for standard date display */ display: '{www}, {dd} {mmm} {yyyy}',
        /** useful for standard datestamps */ weekDate: '{www}, {yyyy}-{mmm}-{dd}',
        /** useful for standard timestamps */ weekTime: '{www}, {yyyy}-{mmm}-{dd} {hh}:{mi}:{ss}',
        /** useful for standard full timestamps */ weekStamp: '{www}, {yyyy}-{mmm}-{dd} {hh}:{mi}:{ss}.{ff}',
        /** useful for readable month and day */ dayMonth: '{dd}-{mmm}',
        /** useful for readable year, month and day */ dayDate: '{dd}-{mmm}-{yyyy}',
        /** display with Time */ dayTime: '{dd}-{mmm}-{yyyy} {hh}:{mi}:{ss}',
        /** useful for stamping logs */ logStamp: '{ymd}T{hms}.{ff}',
        /** useful for sorting display-strings */ sortTime: '{yyyy}-{mm}-{dd} {hh}:{mi}:{ss}',
        /** useful for sorting week order */ yearWeek: '{yw}{wy}',
        /** useful for sorting month order */ yearMonth: '{yyyy}{mm}',
        /** useful for sorting date order */ yearMonthDay: '{ymd}',
        /** just Date portion */ date: '{yyyy}-{mm}-{dd}',
        /** just Time portion */ time: '{hh}:{mi}:{ss}',
    },
    LIMIT: {
        /** Tempo(31-Dec-9999.23:59:59).ns */ get maxTempo() { return Temporal.Instant.from('9999-12-31T23:59:59.999999999+00:00').epochNanoseconds; },
        /** Tempo(01-Jan-1000.00:00:00).ns */ get minTempo() { return Temporal.Instant.from('1000-01-01T00:00+00:00').epochNanoseconds; },
    },
    MONTH_DAY: {
        locales: ['en-US', 'en-AS'],
        layouts: [['dayMonthYearShort', 'monthDayYearShort'], ['dayMonthYear', 'monthDayYear']],
        timezones: {
            'en-US': [
                "America/Adak", "America/Anchorage", "America/Boise", "America/Chicago", "America/Denver",
                "America/Detroit", "America/Indiana/Indianapolis", "America/Indiana/Knox", "America/Indiana/Marengo",
                "America/Indiana/Petersburg", "America/Indiana/Tell_City", "America/Indiana/Vevay", "America/Indiana/Vincennes",
                "America/Indiana/Winamac", "America/Indianapolis", "America/Juneau", "America/Kentucky/Louisville",
                "America/Kentucky/Monticello", "America/Los_Angeles", "America/Louisville", "America/Menominee",
                "America/Metlakatla", "America/New_York", "America/Nome", "America/North_Dakota/Beulah",
                "America/North_Dakota/Center", "America/North_Dakota/New_Salem", "America/Phoenix", "America/Sitka",
                "America/Yakutat", "Pacific/Honolulu",
                "US/Aleutian", "US/Alaska", "US/Arizona", "US/Central", "US/Eastern", "US/Mountain", "US/Pacific",
            ],
            'en-AS': [
                "Pacific/Pago_Pago"
            ]
        }
    },
    LOCALE: {},
};
/** @internal Centralized mutable state for all extendable registries */
export const STATE = {
    NUMBER: allDescriptors(DEFAULTS.NUMBER),
    DURATION: allDescriptors(DEFAULTS.DURATION),
    TIMEZONE: allDescriptors(DEFAULTS.TIMEZONE),
    DURATIONS: allDescriptors(DEFAULTS.DURATIONS),
    FORMAT: allDescriptors(DEFAULTS.FORMAT),
    LIMIT: allDescriptors(DEFAULTS.LIMIT),
    MONTH_DAY: allDescriptors(DEFAULTS.MONTH_DAY),
    LOCALE: allDescriptors(DEFAULTS.LOCALE),
};
const defineExtensible = (target) => Object.defineProperty(target, sym.$Extensible, { value: true, enumerable: false, configurable: false, writable: false });
defineExtensible(STATE.NUMBER);
defineExtensible(STATE.FORMAT);
defineExtensible(STATE.TIMEZONE);
defineExtensible(STATE.DURATION);
defineExtensible(STATE.DURATIONS);
defineExtensible(STATE.MONTH_DAY);
defineExtensible(STATE.LOCALE);
/** Gregorian calendar week-days (short-form) */
export const WEEKDAY = enumify(['All', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
/** Gregorian calendar week-days (long-form) */
export const WEEKDAYS = enumify(['Everyday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']);
/** Gregorian calendar months (short-form) */
export const MONTH = enumify(['All', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']);
/** Gregorian calendar months (long-form) */
export const MONTHS = enumify(['Every', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']);
/** number names (0-10) */
export const NUMBER = looseIndex()(enumify(STATE.NUMBER, false));
/** common time-zone aliases */
export const TIMEZONE = looseIndex()(proxify(STATE.TIMEZONE, true, false));
/** number of seconds in a time unit */
export const DURATION = enumify(STATE.DURATION, false);
/** number of milliseconds in a time unit */
export const DURATIONS = enumify(STATE.DURATIONS, false);
/** common format aliases */
export const FORMAT = looseIndex()(enumify(STATE.FORMAT, false));
export const LIMIT = proxify(STATE.LIMIT, true, false);
/** regional month-day-year parsing settings */
export const MONTH_DAY = proxify(STATE.MONTH_DAY, true, false);
/** localized dictionary translations */
export const LOCALE = proxify(STATE.LOCALE, true, true);
/** date-time element tokens */
const elementKeys = ['yy', 'mm', 'wy', 'ww', 'dd', 'hh', 'mi', 'ss', 'ms', 'us', 'ns'];
export const ELEMENT = enumify({
    yy: 'year',
    mm: 'month',
    wy: 'week',
    ww: 'week',
    dd: 'day',
    hh: 'hour',
    mi: 'minute',
    ss: 'second',
    ms: 'millisecond',
    us: 'microsecond',
    ns: 'nanosecond',
}, false);
/** allowed mutation keys for .set() and .add() */
const mutationKeys = [...elementKeys, 'event', 'period', 'clock', 'time', 'date', 'start', 'mid', 'end'];
export const MUTATION = enumify(mutationKeys, false);
/** allowed keys for ZonedDateTime-like objects */
const zonedDateTimeKeys = ['value', 'timeZoneId', 'calendarId', 'monthCode', 'offset', 'timeZone', 'calendar', 'year', 'month', 'day', 'hour', 'minute', 'second', ...elementKeys];
export const ZONED_DATE_TIME = enumify(zonedDateTimeKeys, false);
/** allowed keys for Tempo configuration options */
const configKeys = ['config', 'parse', 'value', 'intl', 'store', 'discovery', 'debug', 'catch', 'silent', 'timeZone', 'calendar', 'locale', 'sphere', 'timeStamp', 'registry', 'plugins', 'extends', 'cache'];
export const CONFIG = enumify(configKeys, false);
/** initialization strategies */
export const MODE = enumify({ Auto: 'auto', Strict: 'strict', Defer: 'defer', }, false);
/** cache operation modes */
export const CACHE = enumify({
    Off: false,
    On: true,
    Refresh: 'refresh',
}, false);
/** allowed keys for internal parse state */
const parseKeys = ['monthDay', 'planner', 'layoutOrder', 'preFilter', 'mode', 'pivot', 'snippet', 'layout', 'event', 'period', 'anchor'];
export const PARSE = enumify(parseKeys, false);
/** allowed keys for global discovery objects */
const discoveryKeys = ['options', 'extends', 'plugins', 'terms', 'timeZones', 'monthDay', 'intl', 'planner', 'numbers', 'formats', 'ignore'];
export const DISCOVERY = enumify(discoveryKeys, false);
/** license validation status */
export const LICENSE = enumify({
    None: 'none',
    Pending: 'pending',
    Active: 'active',
    Expired: 'expired',
    Revoked: 'revoked',
    Invalid: 'invalid',
    Unauthorized: 'unauthorized',
    Unknown: 'unknown',
}, false);
/** @internal LIVE Registries mapping (STATE key -> Enum/Proxy) */
export const REGISTRIES = {
    NUMBER, DURATION, TIMEZONE, DURATIONS, FORMAT, LIMIT, MONTH_DAY, LOCALE
};
/** public-reachable enums */
export default {
    SEASON,
    COMPASS,
    WEEKDAY,
    WEEKDAYS,
    MONTH,
    MONTHS,
    NUMBER,
    TIMEZONE,
    DURATION,
    DURATIONS,
    FORMAT,
    LIMIT,
    ELEMENT,
    MUTATION,
    ZONED_DATE_TIME,
    CONFIG,
    MODE,
    CACHE,
    PARSE,
    MONTH_DAY,
    LICENSE,
    LOCALE,
};
